<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

type Slot = { starts_at: string; ends_at: string; available: boolean }
type Day = { date: string; available: boolean; slots: Slot[] }
type Availability = { timezone: string; duration_minutes: number; max_date: string; days: Day[] }
const props = defineProps<{ productId: number }>()
const selected = defineModel<string | null>({ default: null })
const { t, locale } = useI18n()
const { $api } = useNuxtApp()
const month = ref(new Date(new Date().getFullYear(), new Date().getMonth(), 1, 12))
const day = ref('')
const data = ref<Availability | null>(null)
const loading = ref(false)
const error = ref(false)
let requestVersion = 0
const isoDay = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
const initialMax = new Date(); initialMax.setFullYear(initialMax.getFullYear() + 1)
const maxDay = computed(() => data.value?.max_date || isoDay(initialMax))
const monthTitle = computed(() => new Intl.DateTimeFormat(locale.value, { month: 'long', year: 'numeric' }).format(month.value))
const weekDays = computed(() => Array.from({ length: 7 }, (_, i) => new Intl.DateTimeFormat(locale.value, { weekday: 'short' }).format(new Date(2026, 9, 5 + i, 12))))
const padding = computed(() => (month.value.getDay() + 6) % 7)
const slots = computed(() => data.value?.days.find(d => d.date === day.value)?.slots || [])
const time = (iso: string) => new Intl.DateTimeFormat(locale.value, { hour: '2-digit', minute: '2-digit', timeZone: data.value?.timezone }).format(new Date(iso))
const canPrevious = computed(() => month.value.getFullYear() > new Date().getFullYear() || month.value.getMonth() > new Date().getMonth())
const canNext = computed(() => isoDay(new Date(month.value.getFullYear(), month.value.getMonth() + 1, 1, 12)) <= maxDay.value)
async function refresh() {
  const version = ++requestVersion
  loading.value = true; error.value = false
  const last = isoDay(new Date(month.value.getFullYear(), month.value.getMonth() + 1, 0, 12))
  try {
    const result = await $api<Availability>(`/products/${props.productId}/booking-availability`, { query: { from: isoDay(month.value), to: last < maxDay.value ? last : maxDay.value } })
    if (version !== requestVersion) return
    data.value = result
    if (!result.days.some(d => d.date === day.value && d.available)) day.value = result.days.find(d => d.available)?.date || ''
    if (selected.value && !result.days.some(d => d.slots.some(s => s.starts_at === selected.value && s.available))) selected.value = null
  } catch {
    if (version === requestVersion) { error.value = true; data.value = null; selected.value = null }
  } finally {
    if (version === requestVersion) loading.value = false
  }
}
function changeMonth(amount: number) {
  month.value = new Date(month.value.getFullYear(), month.value.getMonth() + amount, 1, 12)
  day.value = ''; selected.value = null
  void refresh()
}
function chooseDay(date: string) { day.value = date; selected.value = null }
watch(() => props.productId, () => { selected.value = null; void refresh() })
onMounted(refresh)
defineExpose({ refresh })
</script>

<template>
  <section class="booking-calendar" :aria-label="t('booking_choose_day')" :aria-busy="loading">
    <h3>{{ t('booking_choose_day') }}</h3>
    <div class="booking-month">
      <button type="button" :disabled="!canPrevious || loading" :aria-label="t('booking_previous_month')" @click="changeMonth(-1)">‹</button>
      <strong>{{ monthTitle }}</strong>
      <button type="button" :disabled="!canNext || loading" :aria-label="t('booking_next_month')" @click="changeMonth(1)">›</button>
    </div>
    <p v-if="error" role="alert">{{ t('booking_load_error') }} <button type="button" @click="refresh">{{ t('booking_retry') }}</button></p>
    <div v-else class="booking-days">
      <span v-for="label in weekDays" :key="label" class="weekday">{{ label }}</span>
      <span v-for="n in padding" :key="'pad'+n" />
      <button v-for="item in data?.days || []" :key="item.date" type="button" :disabled="loading || !item.available" :aria-pressed="day === item.date"
              :class="{ selected: day === item.date }" @click="chooseDay(item.date)">{{ Number(item.date.slice(-2)) }}</button>
    </div>
    <div v-if="day" class="booking-slots">
      <button v-for="slot in slots" :key="slot.starts_at" type="button" :disabled="!slot.available || loading" :aria-pressed="selected === slot.starts_at"
              :class="{ selected: selected === slot.starts_at }" @click="selected = slot.starts_at">{{ time(slot.starts_at) }}–{{ time(slot.ends_at) }}</button>
    </div>
    <p v-if="!loading && !error && !day">{{ t('booking_empty') }}</p>
    <p v-if="data" class="booking-help">{{ t('booking_minutes', { minutes: data.duration_minutes }) }}</p>
  </section>
</template>

<style scoped>
.booking-calendar { padding: 1rem; margin: 1rem 0; border: 1px solid var(--el-border-color); border-radius: 12px; max-width: 560px; }
h3 { font-size: 1.1rem; margin: 0 0 1rem; }
.booking-month { display: flex; justify-content: space-between; align-items: center; margin-bottom: .75rem; }
.booking-days { display: grid; grid-template-columns: repeat(7, 1fr); gap: .3rem; }
.weekday { text-align: center; font-size: .8rem; color: var(--el-text-color-secondary); }
button { border: 1px solid var(--el-border-color); background: var(--el-bg-color); color: var(--el-text-color-primary); border-radius: 7px; min-height: 38px; padding: .35rem; cursor: pointer; }
button:hover:not(:disabled), button.selected { border-color: var(--el-color-primary); color: var(--el-color-primary); background: var(--el-color-primary-light-9); }
button:disabled { opacity: .4; cursor: not-allowed; }
button:focus-visible { outline: 2px solid var(--el-color-primary); outline-offset: 2px; }
.booking-slots { display: grid; grid-template-columns: repeat(auto-fit, minmax(105px, 1fr)); gap: .4rem; margin-top: 1rem; }
.booking-help { color: var(--el-text-color-secondary); font-size: .85rem; margin: .85rem 0 0; }
</style>
